import { translate } from "../locales/i18n";
import { parseJson } from "./message_utils";
import { LLMMessageTopicContainer } from "../utils/common";
import { IndexedSet } from "./object_type_utils";

export type DeviationType = "deviation" | "missing" | "suggestion" | "critical";

export interface ClauseReview {
  clause_text: string;
  Action: string;
  Reason: string;
  Suggested_replacement: string;
}
export interface TrafficLightResultObject {
  Overall_deviation: boolean | null;
  Clauses_review: ClauseReview[];
  Missing_required_clause: boolean | null;
  Suggested_insertion: string;
}

export function createTrafficLightResultObject(text: string): TrafficLightResultObject | null {
  if (!text || text.trim().length === 0) {
    return {
      Overall_deviation: null,
      Clauses_review: [],
      Missing_required_clause: null,
      Suggested_insertion: "",
    };
  }

  let parsed: Record<string, any>;
  try {
    parsed = parseJson(text);

    const result: TrafficLightResultObject = {
      Overall_deviation: parsed.hasOwnProperty("Overall_deviation")
        ? Boolean(parsed.Overall_deviation)
        : null,

      Clauses_review: parsed.hasOwnProperty("Clauses_review")
        ? (parsed.Clauses_review as ClauseReview[])
        : [],

      Missing_required_clause: parsed.hasOwnProperty("Missing_required_clause")
        ? Boolean(parsed.Missing_required_clause)
        : null,

      Suggested_insertion: parsed.hasOwnProperty("Suggested_insertion")
        ? String(parsed.Suggested_insertion)
        : "",
    };

    return result;
  } catch (e) {
    console.log("ERROR when parsing", e);
    return null;
  }
}

export class TrafficLighSummary {
  private rootElement: HTMLDivElement;
  private deviationsContainer: HTMLDivElement;
  private editProgressContainer: HTMLDivElement;

  private deviationCards: Map<DeviationType, DeviationCard> = new Map();

  public constructor() {
    this.rootElement = document.createElement("div");
    this.rootElement.classList.add(
      "traffic-light-summary-root",
      "flex",
      "flex-col",
      "sticky-header"
    );

    this.deviationsContainer = document.createElement("div");
    this.deviationsContainer.classList.add("traffic-light-summary-deviations-container");
    this.rootElement.appendChild(this.deviationsContainer);

    this.editProgressContainer = document.createElement("div");
    this.editProgressContainer.classList.add("traffic-light-summary-edit-progress-container");
    this.rootElement.appendChild(this.editProgressContainer);
  }

  public root(): HTMLDivElement {
    return this.rootElement;
  }

  public addDeviationCard(deviationID: DeviationType): DeviationCard {
    const card = new DeviationCard(deviationID);
    this.deviationsContainer.appendChild(card.root());
    this.deviationCards.set(deviationID, card);
    return card;
  }

  public getDeviationCard(
    deviationID: DeviationType,
    create: boolean = false
  ): DeviationCard | undefined {
    if (this.deviationCards.has(deviationID)) {
      return this.deviationCards.get(deviationID);
    } else if (create) {
      const card = this.addDeviationCard(deviationID);
      return card;
    }
    return undefined;
  }

  public clearAllCardAddCount(): void {
    this.deviationCards.forEach((card) => card.clearAddCount());
  }

  public resetAllSubTopicNavigation(): void {
    this.deviationCards.forEach((card) => card.resetSubTopicNavigation());
  }
}

class DeviationCard {
  private deviationID: DeviationType;
  private subTopicContainerSet: IndexedSet<LLMMessageTopicContainer> =
    new IndexedSet<LLMMessageTopicContainer>();
  private addSubTopicContainerSet: IndexedSet<LLMMessageTopicContainer> =
    new IndexedSet<LLMMessageTopicContainer>();

  private rootElement: HTMLDivElement;
  private countElement: HTMLDivElement;
  private textElement: HTMLDivElement;
  private controlContainer: HTMLDivElement;
  private previousButton: HTMLButtonElement;
  private nextButton: HTMLButtonElement;
  private currentIndex: number = -1;

  public constructor(deviationID: DeviationType) {
    this.deviationID = deviationID;

    this.rootElement = document.createElement("div");
    this.rootElement.classList.add(
      "traffic-light-deviation-card",
      "flex",
      "flex-col",
      "flex-content-center",
      this.deviationID
    );

    this.countElement = document.createElement("div");
    this.countElement.classList.add("traffic-light-deviation-count");
    this.countElement.textContent = String(this.subTopicContainerSet.size);
    this.rootElement.appendChild(this.countElement);

    this.textElement = document.createElement("div");
    this.textElement.classList.add("traffic-light-deviation-text");
    this.textElement.textContent = this.getTranslatedText();

    this.controlContainer = document.createElement("div");
    this.controlContainer.classList.add(
      "topic-search-control-container", // reuse
      "traffic-light-deviation-card-control-container",
      "flex",
      "flex-row"
    );
    this.rootElement.appendChild(this.controlContainer);

    this.rootElement.appendChild(this.textElement);

    this.previousButton = document.createElement("button");
    this.previousButton.classList.add("deviation-card-button");
    this.previousButton.innerHTML = '<i class="fa fa-chevron-left"></i>';
    this.previousButton.disabled = true; // Initially disabled
    this.previousButton.onclick = (event) => {
      event.stopPropagation();
      // this.previousButtonClickHandler();
      this.handleSubTopicNavigation(false);
    };
    this.controlContainer.appendChild(this.previousButton);

    this.nextButton = document.createElement("button");
    this.nextButton.classList.add("deviation-card-button");
    this.nextButton.innerHTML = '<i class="fa fa-chevron-right"></i>';
    this.nextButton.onclick = (event) => {
      event.stopPropagation();
      // this.nextButtonClickHandler();
      this.handleSubTopicNavigation(true);
    };
    this.controlContainer.appendChild(this.nextButton);

    // TODO: ENABLE NEW FEATURE
    // this.controlContainer.classList.add("gone");

    this.rootElement.classList.add("gone");
  }

  public root(): HTMLDivElement {
    return this.rootElement;
  }

  private getTranslatedText(): string {
    return this.subTopicContainerSet.size + this.addSubTopicContainerSet.size === 1
      ? translate(this.deviationID)
      : translate(this.deviationID + "-plural");
  }

  public getCount(): number {
    return this.subTopicContainerSet.size;
  }

  public addCount(subTpoicContainerArray: LLMMessageTopicContainer[]): void {
    if (subTpoicContainerArray.length === 0) return;
    this.rootElement.classList.remove("gone");
    this.addSubTopicContainerSet = new IndexedSet<LLMMessageTopicContainer>(subTpoicContainerArray);
    this.countElement.textContent = String(
      this.subTopicContainerSet.size + this.addSubTopicContainerSet.size
    );
    this.textElement.textContent = this.getTranslatedText();
  }

  public clearAddCount(): void {
    this.subTopicContainerSet = new IndexedSet<LLMMessageTopicContainer>([
      ...this.subTopicContainerSet,
      ...this.addSubTopicContainerSet,
    ]);
    this.addSubTopicContainerSet = new IndexedSet<LLMMessageTopicContainer>();
    this.countElement.textContent = String(this.subTopicContainerSet.size);
    this.textElement.textContent = this.getTranslatedText();
    if (this.subTopicContainerSet.size === 0) return;
    this.rootElement.classList.remove("gone");
  }

  public increaseCount(subTopicContainer: LLMMessageTopicContainer): void {
    this.clearAddCount();
    this.subTopicContainerSet.add(subTopicContainer);
    this.countElement.textContent = String(this.subTopicContainerSet.size);
    this.textElement.textContent = this.getTranslatedText();
    this.rootElement.classList.remove("gone");
  }

  public decreaseCount(subTopicContainer: LLMMessageTopicContainer): void {
    this.clearAddCount();
    if (!this.subTopicContainerSet.has(subTopicContainer)) return;
    this.subTopicContainerSet.delete(subTopicContainer);
    this.countElement.textContent = String(this.subTopicContainerSet.size);
    this.textElement.textContent = this.getTranslatedText();
    this.rootElement.classList.remove("gone");
  }

  private handleSubTopicNavigation(next: boolean) {
    if (this.getCount() === 0) return;

    this.currentIndex += next ? 1 : -1;

    if (this.currentIndex <= 0) {
      this.currentIndex = 0;
      this.previousButton.disabled = true;
    } else {
      this.previousButton.disabled = false;
    }

    if (this.currentIndex >= this.getCount() - 1) {
      this.currentIndex = this.getCount() - 1;
      // this.nextButton.disabled = true;
    } else {
      // this.nextButton.disabled = false;
    }

    this.subTopicContainerSet.toArray().forEach(async (subEntry, index) => {
      if (index === this.currentIndex) {
        subEntry.parentTopicContainer?.topicOpen();
        await subEntry.subTopicOpen();
      } else {
        subEntry.parentTopicContainer?.topicClose();
        subEntry.subTopicClose();
      }
    });
  }

  public resetSubTopicNavigation(): void {
    this.currentIndex = -1;
    this.previousButton.disabled = true;
    this.nextButton.disabled = false;
  }
}
